/**
 * Landing-only CSS, injected as a `<style>` rather than added to app.css
 * because none of it is used anywhere in the clinical app.
 *
 * Two mechanisms live here.
 *
 * **Surface contexts.** `.mn-on-oxblood` and `.mn-on-ink` re-declare the
 * semantic tokens for the inverted full-bleed bands, so everything inside them
 * keeps using plain `bg-background` / `text-foreground` / `text-muted-foreground`
 * utilities and stays internally consistent. This only works because app.css
 * registers colours under `@theme inline` — `bg-background` compiles to
 * `background-color: var(--background)` and therefore picks up a subtree
 * override. Under a plain `@theme` it would compile to the literal and every
 * rule below would silently do nothing.
 *
 * The oxblood values are hardcoded triplets rather than `var(--brand-oxblood)`
 * for `--fgc`/`--bgc`, which have to be raw `R G B` to survive
 * `rgb(var(--fgc) / 0.2)`. That is safe here: oxblood carries the same hex in
 * both themes, precisely because it is already dark enough not to need
 * lifting.
 *
 * **Arches.** The brand board's section openers are "radius on top corners
 * only". The obvious `9999px 9999px 0 0` is not stable: the browser scales
 * every radius by `f = min(edge / sum of radii on that edge)`, so an 800x320
 * card resolves to two quarter-circles with 160px of flat between them while
 * the same class on a 360x520 card resolves to a true dome. Percentages
 * resolve against width and height independently, so the proportion holds at
 * every size. `.mn-arch-dome` on a 4:3 box is the exact shape `9999px`
 * produces at 4:3, so hero fidelity is preserved.
 */
const landingCss = `
/* Section openers. Horizontal radii resolve against width, vertical against
   height, so these are size-stable where a px radius is not. */
.mn-arch { border-radius: 50% 50% 0 0 / 30% 30% 0 0; }
.mn-arch-dome { border-radius: 50% 50% 0 0 / 66.6% 66.6% 0 0; }

/* Full-bleed oxblood band — the board's "hero blocks, emphasis".
   --primary becomes mustard here: on a band, the one action we want taken is
   the CTA, and terracotta on oxblood is 2.81:1. */
.mn-on-oxblood {
  --background: #7A2E2A;
  --foreground: #E9D5C7;
  --card: #8B4038;
  --card-foreground: #E9D5C7;
  --muted-foreground: #E9CABF;
  --border: rgb(233 213 199 / 0.16);
  --input: rgb(233 213 199 / 0.28);
  --primary: #E8B23E;
  --primary-foreground: #2E2420;
  --ring: #E8B23E;
  --fgc: 233 213 199;
  --bgc: 122 46 42;
  --ac: 232 178 62;
}

/* The announcement bar and the footer. Literal in both themes: an ink band is
   a fixed treatment, and deriving it from --brand-ink would invert it in dark
   mode, where --brand-ink is the page ground itself. */
.mn-on-ink {
  --background: #2E2420;
  --foreground: #F7EFE4;
  --card: #3A2E29;
  --card-foreground: #F7EFE4;
  --muted-foreground: #C9BBAE;
  --border: rgb(247 239 228 / 0.14);
  --primary: #E8B23E;
  --primary-foreground: #2E2420;
  --ring: #E8B23E;
  --fgc: 247 239 228;
  --bgc: 46 36 32;
  --ac: 232 178 62;
}

/* Reveal-on-scroll. The brand board puts everything under 300ms, so this is
   220/240ms with a 20ms-per-item stagger — 280ms worst case. The previous
   version ran 850-950ms with a filter: blur(6px), which besides being three
   times over budget forced a compositing layer on every one of ~60 targets. */
.mn-landing [data-reveal] {
  opacity: 0;
  transform: translateY(14px);
  transition:
    opacity 220ms ease-out,
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
.mn-landing [data-reveal].mn-revealed {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .mn-landing *,
  .mn-landing *::before,
  .mn-landing *::after {
    animation: none !important;
  }
  .mn-landing [data-reveal] {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
`;

export function LandingStyles() {
    return <style>{landingCss}</style>;
}
